import { useState } from "react";
import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import { Eyebrow } from "../../components/design";
import { VITANARA_BERATUNG_DISPLAY, VITANARA_BERATUNG_TEL } from "../../lib/contact";

type FaqItem = { q: string; a: string };
type FaqGroup = { title: string; items: FaqItem[] };

const GROUPS: FaqGroup[] = [
  {
    title: "Bevor es losgeht",
    items: [
      {
        q: "Was, wenn meine Oma kein Smartphone hat?",
        a: "Genau dafür ist Vitanara gebaut. Ein einfacher Festnetzanschluss reicht — sie nimmt ab, wir rufen an. Mehr nicht.",
      },
      {
        q: "Was, wenn sie nicht mehr gut hört?",
        a: "Konrad und Ida sprechen langsam und deutlich. Sie können die Lautstärke und das Sprechtempo in der App anpassen.",
      },
      {
        q: "Wie weiß sie, dass wir anrufen?",
        a: "Vor dem ersten Anruf bekommt sie eine handgeschriebene Karte mit Erklärung, Datum und Uhrzeit per Post.",
      },
    ],
  },
  {
    title: "Während der Anrufe",
    items: [
      {
        q: "Wie lange dauert ein Anruf?",
        a: "Zwischen 30 und 45 Minuten. Konrad und Ida hören auf, wenn sie merken, dass die Energie nachlässt.",
      },
      {
        q: "Was, wenn sie keine Zeit hat?",
        a: "Kein Problem. Eine Verschiebung ist mit einem Anruf jederzeit möglich, auch spontan.",
      },
      {
        q: "Kann ich mithören?",
        a: "Auf Wunsch der erzählenden Person können Familienmitglieder die Aufnahmen mit einer Verzögerung in der App anhören.",
      },
    ],
  },
  {
    title: "Datenschutz",
    items: [
      {
        q: "Wem gehören die Geschichten?",
        a: "Immer der Familie. Wir nutzen die Aufnahmen ausschließlich, um das Buch zu erstellen. Nichts wird weitergegeben, nichts trainiert öffentlich.",
      },
      {
        q: "Wo werden Daten gespeichert?",
        a: "Auf ISO-zertifizierten Servern in Frankfurt am Main. Verschlüsselung Ende-zu-Ende.",
      },
      {
        q: "Wie kann ich löschen?",
        a: "Auf einen Klick in der App, oder per Anruf. Alle Daten — inklusive Audio — werden binnen 30 Tagen vollständig entfernt.",
      },
    ],
  },
];

export default function FAQ() {
  // Track the open question by global index. First question opens by default.
  const [openKey, setOpenKey] = useState<string>("0-0");

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <PublicHeader />
      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "12px 22px 30px",
          fontFamily: "var(--f-body)",
          color: "var(--ink)",
        }}
      >
        <section style={{ padding: "32px 0 16px" }}>
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h1
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 400,
              fontSize: 38,
              lineHeight: 1.04,
              letterSpacing: "-0.018em",
              margin: "0 0 14px",
              color: "var(--ink)",
            }}
          >
            Was Familien
            <br />
            <em>wissen wollen</em>.
          </h1>
          <p
            style={{
              fontFamily: "var(--f-body)",
              fontSize: 16,
              lineHeight: 1.45,
              color: "var(--ink-soft)",
              margin: 0,
            }}
          >
            Falls Ihre Frage nicht dabei ist — rufen Sie uns an. Wirklich.
          </p>
        </section>

        {GROUPS.map((g, gi) => (
          <section key={g.title} style={{ padding: "12px 0 8px" }}>
            <div
              style={{
                fontFamily: "var(--f-display)",
                fontStyle: "italic",
                fontSize: 22,
                color: "var(--oxblood)",
                margin: "12px 0 14px",
                letterSpacing: "-0.01em",
              }}
            >
              {g.title}
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {g.items.map((it, i) => {
                const key = `${gi}-${i}`;
                const open = openKey === key;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setOpenKey(open ? "" : key)}
                    aria-expanded={open}
                    style={{
                      textAlign: "left",
                      background: "var(--paper-50)",
                      border: "1px solid rgba(43,26,15,0.10)",
                      borderRadius: 10,
                      padding: "14px 16px",
                      cursor: "pointer",
                      width: "100%",
                      font: "inherit",
                      color: "inherit",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          fontFamily: "var(--f-display)",
                          fontWeight: 500,
                          fontSize: 16,
                          color: "var(--ink)",
                          lineHeight: 1.35,
                        }}
                      >
                        {it.q}
                      </div>
                      <div
                        aria-hidden
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: open ? "var(--oxblood)" : "transparent",
                          border: open
                            ? "none"
                            : "1px solid rgba(43,26,15,0.20)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: open ? "var(--paper-50)" : "var(--ink-soft)",
                          fontFamily: "var(--f-ui)",
                          fontSize: 14,
                          fontWeight: 600,
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        {open ? "−" : "+"}
                      </div>
                    </div>
                    {open && (
                      <p
                        style={{
                          fontFamily: "var(--f-body)",
                          fontSize: 15,
                          lineHeight: 1.5,
                          color: "var(--ink-soft)",
                          margin: "10px 0 0",
                          paddingRight: 32,
                        }}
                      >
                        {it.a}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        <section style={{ padding: "24px 0 16px" }}>
          <div
            style={{
              background: "var(--paper-200)",
              borderRadius: 14,
              padding: "20px 20px",
              border: "1px solid rgba(43,26,15,0.10)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "var(--oxblood)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-hidden
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--paper-50)"
                strokeWidth="1.8"
              >
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontFamily: "var(--f-display)",
                  fontWeight: 500,
                  fontSize: 16,
                  color: "var(--ink)",
                }}
              >
                Wir rufen Sie zurück
              </div>
              <div
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 13.5,
                  color: "var(--ink-soft)",
                  marginTop: 2,
                }}
              >
                <a
                  href={`tel:${VITANARA_BERATUNG_TEL}`}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {VITANARA_BERATUNG_DISPLAY}
                </a>{" "}
                · Mo–Fr 9–18 Uhr
              </div>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
